import React, { useState } from 'react';
import { Box, TextField, MenuItem, Button, Paper } from '@mui/material';

const INDUSTRY_OPTIONS = ['IT', 'Healthcare', 'Finance', 'Manufacturing', 'Retail'];

const EXPERIENCE_OPTIONS = [
  { value: 'entry', label: 'Entry Level' },
  { value: 'mid', label: 'Mid Level' },
  { value: 'senior', label: 'Senior' },
  { value: 'lead', label: 'Lead' },
];

const POSTED_WITHIN_OPTIONS = [
  { value: '1', label: 'Last 24 hours' },
  { value: '3', label: 'Last 3 days' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
];

const emptyFilters = {
  jobTitle: '',
  technology: '',
  location: '',
  industry: '',
  experience: '',
  postedWithinDays: '',
  fromDate: '',
  toDate: '',
};

const FIELD_FONT_SIZE = 13;

// Deliberately no forced `height` here. MUI's outlined variant computes the shrunk
// label's vertical offset from the input's own padding, not from a fixed container
// height — overriding height directly (as this used to do) desyncs that math and is
// exactly what caused the label to overlap the date fields' native placeholder. Setting
// padding here instead keeps everything compact while staying in sync with the label.
const fieldSx = {
  '& .MuiOutlinedInput-input': { paddingTop: '8.5px', paddingBottom: '8.5px', fontSize: FIELD_FONT_SIZE },
  '& .MuiInputLabel-root': { fontSize: FIELD_FONT_SIZE },
};

// Buttons don't have this label-offset problem, so they can still use an explicit height —
// matched here to what the padding above naturally produces (~38px) so the row lines up.
const BUTTON_HEIGHT = 38;

export default function SavedJobsFilterBar({ onApply }) {
  const [filters, setFilters] = useState(emptyFilters);

  const handleChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleApply = () => onApply(filters);
  const handleReset = () => {
    setFilters(emptyFilters);
    onApply(emptyFilters);
  };

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2.5, mb: 3, borderRadius: 2, display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}
    >
      <TextField
        size="small"
        label="Job Title"
        value={filters.jobTitle}
        onChange={handleChange('jobTitle')}
        sx={{ flex: '1 1 150px', minWidth: 130, ...fieldSx }}
      />

      <TextField
        size="small"
        label="Technology"
        placeholder="e.g. React, Java"
        value={filters.technology}
        onChange={handleChange('technology')}
        sx={{ flex: '1 1 150px', minWidth: 130, ...fieldSx }}
      />

      <TextField
        size="small"
        label="Location"
        placeholder="City, state or country"
        value={filters.location}
        onChange={handleChange('location')}
        sx={{ flex: '1 1 150px', minWidth: 130, ...fieldSx }}
      />

      <TextField
        select
        size="small"
        label="Industry"
        value={filters.industry}
        onChange={handleChange('industry')}
        sx={{ flex: '1 1 130px', minWidth: 120, ...fieldSx }}
      >
        <MenuItem value="">All</MenuItem>
        {INDUSTRY_OPTIONS.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
      </TextField>

      <TextField
        select
        size="small"
        label="Experience"
        value={filters.experience}
        onChange={handleChange('experience')}
        sx={{ flex: '1 1 130px', minWidth: 120, ...fieldSx }}
      >
        <MenuItem value="">All</MenuItem>
        {EXPERIENCE_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
      </TextField>

      <TextField
        select
        size="small"
        label="Posted"
        value={filters.postedWithinDays}
        onChange={handleChange('postedWithinDays')}
        sx={{ flex: '1 1 140px', minWidth: 140, ...fieldSx }}
      >
        <MenuItem value="">Any time</MenuItem>
        {POSTED_WITHIN_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
      </TextField>

      <TextField
        size="small"
        type="date"
        // label="Saved From"
        value={filters.fromDate}
        onChange={handleChange('fromDate')}
        InputLabelProps={{ shrink: true }}
        sx={{ flex: '1 1 130px', minWidth: 130, ...fieldSx }}
      />

      <TextField
        size="small"
        type="date"
        // label="Saved To"
        value={filters.toDate}
        onChange={handleChange('toDate')}
        InputLabelProps={{ shrink: true }}
        sx={{ flex: '1 1 130px', minWidth: 130, ...fieldSx }}
      />

      <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleReset}
          sx={{ height: BUTTON_HEIGHT, fontSize: FIELD_FONT_SIZE }}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          sx={{ height: BUTTON_HEIGHT, fontSize: FIELD_FONT_SIZE }}
        >
          Apply filters
        </Button>
      </Box>
    </Paper>
  );
}